/**
 * Recovery for failed dynamic-import / code-split chunk loads.
 *
 * Watchtower lazy-loads every route via React Router's `lazy: () => import(...)`.
 * When a new version is deployed, the content-hashed chunk files referenced by an
 * already-open tab are deleted, so the next navigation to a not-yet-loaded route
 * throws a chunk-load error. A full page reload fetches the fresh `index.html`
 * and its current chunks, recovering transparently.
 *
 * Detecting the failure across every browser's phrasing lives in the shared
 * `isChunkLoadError` (`@edgetier/utilities`) so watchtower and agent-interface
 * stay in sync; this module keeps only the watchtower-specific recovery
 * (throttled reload). Re-exported here so existing importers of this path are
 * unaffected.
 *
 * Message matching alone is not enough: a chunk can be fetched successfully and
 * still fail to *parse*, which rejects with a bare `SyntaxError` that names no
 * chunk and carries no stack (GAS-1043). {@link importOrReload} therefore tags
 * rejections at the import boundary, and the error boundary reads the tag back
 * via the re-exported `isModuleParseFailure`.
 */

import { markDynamicImportFailure } from "@edgetier/utilities";
import { ChunkRecoveryOutcome } from "./chunk-load-error.types";

export { getDynamicImportFailure, isChunkLoadError, isModuleParseFailure } from "@edgetier/utilities";
export type { ChunkRecoveryOutcome } from "./chunk-load-error.types";

/**
 * Key/window used to throttle chunk-error reloads. Stored in `sessionStorage`
 * so the throttle survives the reload it triggers.
 */
const RELOAD_TIMESTAMP_KEY = "watchtower:chunk-load-reload-at";
const RELOAD_THROTTLE_MS = 10_000;

/**
 * Indirection around `window.location.reload` so the recovery path can be
 * spied on in tests — jsdom marks `location` and `location.reload` as
 * non-configurable, so they cannot be replaced directly.
 */
export const chunkLoadRecovery = {
    reload: (): void => window.location.reload(),
};

/**
 * Reloads the page to recover from a chunk-load error, at most once per
 * `RELOAD_THROTTLE_MS`. Returns whether a reload was actually triggered.
 *
 * The time window prevents an infinite reload loop when a chunk is genuinely
 * unreachable (offline, a truly missing file) rather than merely stale: the
 * second failure within the window returns `false` and is left for the caller
 * to handle (show the crash UI, report to Sentry). A single stale-deploy reload
 * normally succeeds, so this path rarely gives up in practice.
 *
 * The throttle depends on `sessionStorage` persisting across the reload. If it is
 * unavailable (e.g. Safari private mode) we cannot guarantee we won't loop, so we
 * decline to reload and let the caller show the crash UI (prompting a manual
 * refresh) rather than risk a tight reload loop on a genuinely missing chunk.
 *
 * @returns {ChunkRecoveryOutcome} What the attempt did, for reporting when it did not recover: `reloaded` when the
 * page is reloading (the error never reaches the boundary), `throttled` when a reload was already attempted and did
 * not fix it — so the same bytes are being re-served and reloading again would only loop — or `storage-unavailable`
 * when the throttle could not be recorded, so no reload was risked at all.
 */
export const attemptChunkErrorReload = (): ChunkRecoveryOutcome => {
    const now = Date.now();

    let previous: number;
    try {
        previous = Number(window.sessionStorage.getItem(RELOAD_TIMESTAMP_KEY));
    } catch {
        return "storage-unavailable";
    }

    if (Number.isFinite(previous) && previous > 0 && now - previous < RELOAD_THROTTLE_MS) {
        return "throttled";
    }

    try {
        window.sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(now));
    } catch {
        return "storage-unavailable";
    }

    chunkLoadRecovery.reload();
    return "reloaded";
};

/**
 * Whether a reload was triggered to recover from a chunk-load error. Thin wrapper
 * over {@link attemptChunkErrorReload} for callers that only need to know whether
 * the page is going away.
 *
 * @returns {boolean} `true` if a reload was triggered, `false` if throttled or unavailable.
 */
export const reloadOnceForChunkError = (): boolean => attemptChunkErrorReload() === "reloaded";

/**
 * Wraps a route's dynamic `import()` so a failed chunk load recovers by reloading
 * the page instead of crashing. A rejection from `factory()` is, by definition, a
 * failed dynamic import (a stale-deploy chunk miss or a transient network error),
 * so recovery needs no error-message matching — this is React Router's recommended
 * pattern (discussion #10333: the `catch` is scoped to the import, so it does not
 * rely on error-prone string matching across browsers).
 *
 * On failure it reloads once (throttled via {@link attemptChunkErrorReload}). While
 * the page navigates it returns a never-resolving promise so the router/Suspense
 * keeps showing the loading state rather than flashing the crash UI. If the reload
 * is throttled — a prior reload did not recover — it rethrows the **original**
 * error, with `name` and `message` untouched, so the route's error boundary
 * (`SentryErrorFallback`) reports it accurately rather than mislabelling a genuine
 * module-initialisation bug as a `ChunkLoadError`.
 *
 * The rethrown error is *tagged* as having come from an import
 * (`markDynamicImportFailure`) so the boundary can tell the two apart without
 * matching error strings. That matters because a chunk whose bytes are not valid
 * JavaScript fails to parse rather than to fetch, rejecting with a bare
 * `SyntaxError: Invalid or unexpected token` that no chunk-message pattern
 * recognises and no stack frame attributes (GAS-1043). The tag also carries the
 * module that failed and what recovery did, since neither is recoverable from the
 * error itself.
 *
 * Note this deliberately reloads on *any* import rejection, not only recognised
 * chunk messages — that keeps recovery independent of brittle cross-browser error
 * strings. The only cost is one (throttled) reload for the rare case of a module
 * that fails to initialise for a non-chunk reason; the original error still
 * surfaces intact afterwards.
 *
 * Recovery covers the *import*, not the factory itself: `factory()` is called
 * outside the `try` so a synchronous throw (a programmer error before the
 * `import()` promise even exists) propagates untouched, with no reload attempted
 * and no tag added. Only the import's own rejection is treated as an asset
 * failure.
 *
 * @param factory - Produces the dynamic import promise (e.g. `() => import("pages/home")`).
 * @param moduleLabel - Identifies the module for reporting, e.g. the route key.
 * @returns The resolved module, or a never-resolving promise while reloading.
 */
export const importOrReload = async <T>(factory: () => Promise<T>, moduleLabel?: string): Promise<T> => {
    // Deliberately outside the try — see above.
    const importing = factory();

    try {
        return await importing;
    } catch (error) {
        const recoveryOutcome = attemptChunkErrorReload();

        if (recoveryOutcome === "reloaded") {
            // Page is reloading — hold the loading state until it navigates away.
            return new Promise<T>(() => {});
        }

        throw markDynamicImportFailure(error, { moduleLabel, recoveryOutcome });
    }
};
